import { Accessor, Layer, LayerProps, PickingInfo } from '@deck.gl/core/typed';
// @ts-expect-error @ts-ignore
import { ClipExtension } from '@deck.gl/extensions';
// @ts-expect-error @ts-ignore
import { TileLayer } from '@deck.gl/geo-layers';
// @ts-expect-error @ts-ignore
import { BitmapLayer, GeoJsonLayer, TextLayer } from '@deck.gl/layers';
import { ScatterplotLayer } from '@deck.gl/layers/typed';
import { MapboxLayer } from '@deck.gl/mapbox/typed';
import { MapboxLayerProps } from '@deck.gl/mapbox/typed/mapbox-layer';
import { h2R } from '@studio/utils';
import { type Geometry, Position } from '@turf/turf';

import { GEOSJON_COLOR_DEFAULT } from '../constants';

export interface AddTextLayerProps {
  data: MapboxLayerProps<Layer>['data'];
  options: { id: string };
  getText: (d: { text: string; position: Geometry['coordinates'] }) => string;
  getPosition: (d: {
    text: string;
    position: Geometry['coordinates'];
  }) => Geometry['coordinates'];
  zoom: number;
  beforeId?: string;
}

export interface AddGeoJSONLayerProps {
  data: MapboxLayerProps<Layer>['data'];
  options: { id: string };
  onClick?: LayerProps['onClick'];
  onHover?: LayerProps['onHover'];
  fillColor?: string;
  updateTriggers?: LayerProps['updateTriggers'];
  getFillColor?: (d: { properties: { [key: string]: string } }) => number[];
  getHighlightColor?: (d: PickingInfo) => number[];
  getLineColor?: (d: { properties: { [key: string]: string } }) => number[];
  beforeId?: string;
  autoHighlight: boolean;
}

interface CreateScatterPlotLayerProps<T> {
  getPosition?: Accessor<T, Position>;
  getRadius?: Accessor<T, number>;
  id?: string;
}

export function createScatterPlotLayer<T = MapboxLayerProps<Layer>['data']>(
  data: T,
  {
    getPosition,
    getRadius,
    id = 'scatterplot-layer'
  }: CreateScatterPlotLayerProps<T>
) {
  const layer = new MapboxLayer({
    id,
    // @ts-expect-error @ts-ignore
    type: ScatterplotLayer,
    data,
    stroked: true,
    getPosition,
    getRadius,
    getFillColor: [255, 140, 0],
    onHover: function ({ index }) {
      if (layer.props.highlightedObjectIndex !== index) {
        layer.setProps({ highlightedObjectIndex: index, id });
      }
    },
    getLineColor: [0, 0, 0],
    getLineWidth: 10,
    radiusScale: 6,
    pickable: true,
    renderingMode: '3d',
    highlightColor: [0, 255, 0]
  });

  return layer;
}

export function createTileLayer<T = MapboxLayerProps<Layer>['data']>(
  data: T,
  {
    id = 'tile-layer',
    opacity = 1,
    bounds = null
  }: {
    id: string;
    opacity?: number;
    bounds?: [number, number, number, number] | null;
  },
  fetch?: (...args: any[]) => any
) {
  const clipProps = bounds
    ? { extensions: [new ClipExtension()], clipBounds: bounds }
    : {};

  const layer = new MapboxLayer({
    id,
    // @ts-expect-error @ts-ignore
    opacity: [opacity],
    type: TileLayer,
    data,
    minZoom: 3,
    maxZoom: 19,
    tileSize: 256,
    fetch: fetch,
    extent: bounds,
    // @ts-expect-error @ts-ignore
    renderSubLayers: props => {
      const {
        bbox: { west, south, east, north }
      } = props.tile;
      const bounds = [west, south, east, north] as [
        number,
        number,
        number,
        number
      ];

      return new BitmapLayer(props, {
        data: null,
        image: props.data,
        bounds
      });
    },
    onTileError: () => {
      return null;
    },
    ...clipProps
  });

  return layer;
}

export function createBitmapLayer(
  url: string,
  bounds: [number, number, number, number],
  { id = 'tile-layer' }: { id: string },
  fetch?: (...args: any[]) => any
) {
  const layer = new MapboxLayer({
    id,
    type: BitmapLayer,
    minZoom: 3,
    maxZoom: 19,
    tileSize: 256,
    // @ts-expect-error @ts-ignore
    opacity: [1],
    image: url,
    bounds,
    fetch: fetch,
    onTileError: () => {
      return null;
    }
  });

  return layer;
}

export function createGeojsonLayer({
  data,
  options: { id = 'geojson-layer' },
  onClick,
  onHover,
  fillColor = GEOSJON_COLOR_DEFAULT,
  updateTriggers,
  getFillColor,
  getHighlightColor,
  getLineColor,
  autoHighlight = false
}: AddGeoJSONLayerProps) {
  const PolygonFill = h2R(fillColor) as {
    r: number;
    g: number;
    b: number;
  };

  const props: ConstructorParameters<typeof MapboxLayer>[0] = {
    id,
    // @ts-expect-error @ts-ignore
    type: GeoJsonLayer,
    data,
    stroked: true,
    filled: true,
    lineWidthScale: 0.5,
    lineWidthMinPixels: 2,
    lineJointRounded: true,
    getLineWidth: 1,
    pickable: true,
    autoHighlight: autoHighlight,
    highlightColor: getHighlightColor
      ? getHighlightColor
      : [PolygonFill.r, PolygonFill.g, PolygonFill.b, 200],
    getFillColor: getFillColor
      ? getFillColor
      : [PolygonFill.r, PolygonFill.g, PolygonFill.b, 120],
    getLineColor: getLineColor
      ? getLineColor
      : [PolygonFill.r, PolygonFill.g, PolygonFill.b, 200],
    onClick,
    onHover,
    extruded: false,
    getElevation: () => 0
  };

  if (updateTriggers) props.updateTriggers = updateTriggers;

  const layer = new MapboxLayer(props);
  return layer;
}

export function createTextLayer({
  data,
  options: { id = 'geojson-text-layer' },
  getText,
  getPosition,
  zoom
}: AddTextLayerProps) {
  const fontSize = 2;
  const sizeMaxPixels = (2 ** zoom / 3) * fontSize;
  const sizeMinPixels = Math.min(2 ** zoom / 1000, 0.5) * fontSize;

  const layer = new MapboxLayer({
    id,
    // @ts-expect-error @ts-ignore
    type: TextLayer,
    data,
    characterSet: 'auto',
    fontSettings: {
      buffer: 8,
      sdf: true
    },
    getText,
    getPosition,
    getSize: 5,
    sizeUnits: 'pixels',
    getColor: [255, 255, 255, 255],
    sizeScale: fontSize,
    pickable: true,
    maxWidth: 64 * 12,
    collisionEnabled: false,
    outlineWidth: 4,
    outlineColor: [0, 0, 0, 255],
    // zoom based params
    sizeMaxPixels,
    sizeMinPixels,
    // Ensure text renders on top of other layers
    parameters: {
      depthTest: false,
      depthMask: false,
      blend: true,
      blendFunc: [770, 771, 1, 771],
      polygonOffsetFill: true,
      polygonOffset: [-1, -1]
    }
  });

  return layer;
}
